import React from 'react';
import styled from 'styled-components/macro';
import { Typography } from '@deeptrue-frontend/ui';
import { DataType } from './Bar';
import Sentiments from '../Sentiments';
import { getTranslation, LocaleType } from '../utils/getTranslations';
import { getNumbericLabel } from '../utils/getNumbericLabel';

const Container = styled.div<{
  isDontKnowSegment: boolean;
  hasDontKnowSegmentInData: boolean;
  isNps: boolean;
}>`
  display: flex;
  flex-direction: column;
  position: relative;
  padding: ${({ hasDontKnowSegmentInData, isNps }) =>
    hasDontKnowSegmentInData || isNps ? '0 1.2rem 1.2rem' : '0 2.5rem 1.2rem'};
  margin-left: ${({ isDontKnowSegment }) => (isDontKnowSegment ? '2rem' : '0')};
`;

const Label = styled(Typography)`
  white-space: nowrap;
`;

const Bars = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 100%;
`;

const Bar = styled.div<{
  height: string;
  bgColor: string;
  isDontKnowSegment: boolean;
  width: string;
  isDetailed: boolean | undefined;
}>`
  width: ${({ width }) => width};
  height: ${({ height }) => height};
  background-color: ${({ bgColor }) => bgColor};
  position: relative;
  margin-right: 2rem;
  margin-bottom: ${({ isDetailed }) => (isDetailed ? '2rem' : '0')};
  min-height: 1px;
  border: ${({ isDontKnowSegment }) =>
    isDontKnowSegment ? '1px solid #C7CAD1' : 'none'};

  &:last-child {
    margin-right: 0;
  }
`;

const BarPercentage = styled(Typography)`
  position: absolute;
  top: -1.5rem;
  left: 50%;
  transform: translate(-50%, 0);
  font-size: 0.875rem;
  font-weight: 700;
`;

const BarValue = styled(Typography)<{ isDetailed: boolean | undefined }>`
  position: absolute;
  bottom: ${({ isDetailed }) => (isDetailed ? '-4.5rem' : '-2rem')};
  left: 50%;
  transform: translate(-50%, 0);
  font-size: ${({ isDetailed }) => (isDetailed ? '0.75rem' : '1.125rem')};
  font-weight: ${({ isDetailed }) => (isDetailed ? '400' : '500')};
  width: ${({ isDetailed }) => (isDetailed ? '100%' : null)};
  text-align: ${({ isDetailed }) => (isDetailed ? 'center' : null)};
`;

const SentimentLine = styled.div<{ bgColor: string }>`
  width: 100%;
  height: 1px;
  background-color: ${({ bgColor }) => bgColor};
  margin: 3rem 0 1.5rem;
`;

interface Props {
  segmentLabel: string;
  segmentPercentValue: number;
  data: DataType[];
  segmentBgColor: string;
  isDontKnowSegment: boolean;
  locale: LocaleType;
  isNPS: boolean;
  hasDontKnowSegmentInData: boolean;
  isDetailed?: boolean;
  width: string;
}

const BarSegment = ({
  segmentLabel,
  segmentPercentValue,
  data,
  segmentBgColor,
  isDontKnowSegment,
  locale,
  isNPS,
  hasDontKnowSegmentInData,
  isDetailed,
  width,
}: Props) => {
  const dontKnowSentiment = '99';

  return (
    <Container
      isDontKnowSegment={isDontKnowSegment}
      hasDontKnowSegmentInData={hasDontKnowSegmentInData}
      isNps={isNPS}
    >
      <Bars>
        {data.map((bar: DataType) => {
          const barLabel = getTranslation(bar.label, locale);
          const bgColor = Sentiments.get(parseInt(bar.sentiment)).color;
          const barValue = isDetailed
            ? barLabel
            : bar.sentiment === dontKnowSentiment
            ? ''
            : getNumbericLabel(barLabel);

          return (
            <Bar
              key={barLabel}
              height={`${bar.value}%`}
              bgColor={bgColor}
              isDontKnowSegment={isDontKnowSegment}
              width={width}
              isDetailed={isDetailed}
            >
              <BarPercentage>{bar.value}%</BarPercentage>
              <BarValue isDetailed={isDetailed}>{barValue}</BarValue>
            </Bar>
          );
        })}
      </Bars>
      <SentimentLine bgColor={segmentBgColor} />
      <Typography fontSize="0.875rem" fontWeight="700">
        {segmentPercentValue}%
      </Typography>
      <Label fontSize="0.875rem" fontWeight="400">
        {segmentLabel}
      </Label>
    </Container>
  );
};

export default BarSegment;
